"use client";

import { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";

const DRAFT_PREFIX = "pguh:formDraft:v2:";
let restoringFieldEvent = false;

function isSupportedField(element) {
  if (!element) return false;
  if (element.dataset?.noDraft === "true") return false;
  if (element.disabled || element.readOnly) return false;

  const tagName = element.tagName?.toLowerCase();
  if (!["input", "textarea", "select"].includes(tagName)) return false;

  if (tagName === "input") {
    const type = String(element.type || "text").toLowerCase();

    if (
      [
        "password",
        "file",
        "hidden",
        "submit",
        "button",
        "reset",
        "image",
      ].includes(type)
    ) {
      return false;
    }

    const autocomplete = String(element.autocomplete || "").toLowerCase();
    if (
      ["current-password", "new-password", "one-time-code"].includes(
        autocomplete,
      )
    ) {
      return false;
    }

    const identifyingText = `${element.name || ""} ${element.id || ""}`.toLowerCase();
    if (/password|passcode|token|secret|one[-_ ]?time|otp/.test(identifyingText)) {
      return false;
    }
  }

  return true;
}

function getFields() {
  return Array.from(document.querySelectorAll("input, textarea, select")).filter(
    isSupportedField,
  );
}

function fieldKey(element, fields) {
  if (element.dataset?.draftKey) return `data:${element.dataset.draftKey}`;
  if (element.id) return `id:${element.id}`;

  const form = element.form;
  const forms = Array.from(document.forms || []);
  const formIndex = form ? forms.indexOf(form) : -1;
  const tag = element.tagName.toLowerCase();
  const type = element.type ? `:${element.type}` : "";
  const name = element.name ? `:name=${element.name}` : "";
  const placeholder = element.placeholder
    ? `:placeholder=${element.placeholder}`
    : "";
  const ariaLabel = element.getAttribute("aria-label")
    ? `:aria=${element.getAttribute("aria-label")}`
    : "";

  if (name || placeholder || ariaLabel) {
    return `field:${formIndex}:${tag}${type}${name}${placeholder}${ariaLabel}`;
  }

  return `auto:${formIndex}:${tag}${type}:${fields.indexOf(element)}`;
}

function readField(element) {
  const type = String(element.type || "").toLowerCase();

  if (type === "checkbox" || type === "radio") {
    return {
      kind: "checked",
      value: Boolean(element.checked),
    };
  }

  return {
    kind: "value",
    value: element.value ?? "",
  };
}

function setNativeValue(element, value) {
  const tagName = element.tagName.toLowerCase();
  const prototype =
    tagName === "textarea"
      ? window.HTMLTextAreaElement?.prototype
      : tagName === "select"
        ? window.HTMLSelectElement?.prototype
        : window.HTMLInputElement?.prototype;

  const descriptor = prototype
    ? Object.getOwnPropertyDescriptor(prototype, "value")
    : null;

  if (descriptor?.set) {
    descriptor.set.call(element, value);
  } else {
    element.value = value;
  }
}

function setNativeChecked(element, checked) {
  const descriptor = Object.getOwnPropertyDescriptor(
    window.HTMLInputElement.prototype,
    "checked",
  );

  if (descriptor?.set) {
    descriptor.set.call(element, Boolean(checked));
  } else {
    element.checked = Boolean(checked);
  }
}

function dispatchFieldEvents(element) {
  restoringFieldEvent = true;
  try {
    element.dispatchEvent(new Event("input", { bubbles: true }));
    element.dispatchEvent(new Event("change", { bubbles: true }));
  } finally {
    restoringFieldEvent = false;
  }
}

function saveDraft(storageKey) {
  const fields = getFields();
  if (!fields.length) return;

  const values = {};

  fields.forEach((element) => {
    values[fieldKey(element, fields)] = readField(element);
  });

  try {
    localStorage.setItem(
      storageKey,
      JSON.stringify({
        savedAt: Date.now(),
        values,
      }),
    );
  } catch (error) {
    console.warn("Unable to save page draft:", error);
  }
}

function readDraft(storageKey) {
  try {
    const stored = JSON.parse(localStorage.getItem(storageKey) || "null");
    return stored?.values && typeof stored.values === "object" ? stored : null;
  } catch {
    return null;
  }
}

function restoreDraft(storageKey, touchedKeys = new Set()) {
  const stored = readDraft(storageKey);
  if (!stored) return false;

  const fields = getFields();
  let restoredAny = false;

  fields.forEach((element) => {
    const key = fieldKey(element, fields);
    if (touchedKeys.has(key)) return;

    const saved = stored.values[key];
    if (!saved) return;

    if (saved.kind === "checked") {
      if (element.checked !== Boolean(saved.value)) {
        setNativeChecked(element, saved.value);
        dispatchFieldEvents(element);
      }
      restoredAny = true;
      return;
    }

    const nextValue = saved.value ?? "";

    if (element.tagName.toLowerCase() === "select") {
      const optionExists = Array.from(element.options || []).some(
        (option) => option.value === String(nextValue),
      );
      if (!optionExists && nextValue !== "") return;
    }

    if (String(element.value ?? "") !== String(nextValue)) {
      setNativeValue(element, nextValue);
      dispatchFieldEvents(element);
    }

    restoredAny = true;
  });

  return restoredAny;
}

export default function FormDraftPersistence({ session }) {
  const location = useLocation();
  const route = `${location.pathname}${location.search}`;
  const ownerKey = session?.user?.id || "public";

  const storageKey = useMemo(
    () => `${DRAFT_PREFIX}${ownerKey}:${encodeURIComponent(route)}`,
    [ownerKey, route],
  );

  useEffect(() => {
    const touchedKeys = new Set();
    const hasStoredDraft = Boolean(readDraft(storageKey));
    const timers = [];

    const attemptRestore = () => {
      if (!hasStoredDraft) return;
      restoreDraft(storageKey, touchedKeys);
    };

    if (hasStoredDraft) {
      [0, 80, 200, 450, 900, 1600, 2600, 4000].forEach((delay) => {
        timers.push(window.setTimeout(attemptRestore, delay));
      });
    }

    // Keep watching for forms/modals that mount later. This is especially
    // useful on management pages where a user can refresh, reopen the same
    // modal, and continue typing from the localStorage draft.
    const observer = new MutationObserver(() => {
      attemptRestore();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    const handleInput = (event) => {
      if (restoringFieldEvent || !isSupportedField(event.target)) return;

      const fields = getFields();
      touchedKeys.add(fieldKey(event.target, fields));
      saveDraft(storageKey);
    };

    const handleBeforeUnload = () => {
      saveDraft(storageKey);
    };

    const handleSubmit = () => {
      // Save once more before React handles the form. If the submission fails,
      // the user's latest values are still available after a refresh.
      saveDraft(storageKey);
    };

    document.addEventListener("input", handleInput, true);
    document.addEventListener("change", handleInput, true);
    document.addEventListener("submit", handleSubmit, true);
    window.addEventListener("beforeunload", handleBeforeUnload);

    const interval = window.setInterval(() => saveDraft(storageKey), 1000);

    return () => {
      saveDraft(storageKey);
      document.removeEventListener("input", handleInput, true);
      document.removeEventListener("change", handleInput, true);
      document.removeEventListener("submit", handleSubmit, true);
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.clearInterval(interval);
      timers.forEach((timer) => window.clearTimeout(timer));
      observer.disconnect();
    };
  }, [storageKey]);

  return null;
}
